import React, { CSSProperties, useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useQuery } from '@tanstack/react-query';
import { Player } from './Player';
import type { PlayerProps } from './types';
import { fetchCreativeWorkData, type CreativeWorkData, CreativeWorksSortDirection } from './api/creative-work';
import { fetchVideosList, Playlist, type VideoData, type ConcurrencySession, ConcurrencyConnectionState } from './api/video';
import { Loading, ErrorScreen, TitleAndDescription } from './components';
import { findHLSPlaylist } from './helper';
import useMessages, { type Translations } from './messages/useMessages';
import { useConcurrencySession, type SessionStartedInfo } from './hooks/useConcurrencySession';
import { getDeviceId } from './utils/deviceId';

export interface CreativeWorkProps extends Omit<PlayerProps, 'src' | 'drmConfig'> {
  publicKey: string;
  creativeWorkId: string;
  hideTitle?: boolean;
  locale?: string;
  filter?: string;
  order?: CreativeWorksSortDirection;
  events?: {
    onCreativeWorkData?: (creativeWork: CreativeWorkData) => void;
    onVideoData?: (video: VideoData) => void;
    onEmptyPlaylists?: () => void;
    onError?: (error: Error) => void;
    onPlay?: () => void;
    onPause?: () => void;
    onEnded?: () => void;
    onLoadStart?: () => void;
    onCanPlay?: () => void;
    /** Fires once the Shaka player instance is created (before manifest load).
     * The instance exposes `player.mux.updateData(...)` for mid-view metadata
     * updates (e.g. attaching consent-gated ids after a CMP grant). */
    onPlayerReady?: (player: any) => void;
    onAdStart?: () => void;
    onAdComplete?: () => void;
    onAdError?: (error: any) => void;
    onAdSkipped?: () => void;
    onAdPaused?: () => void;
    onAdResumed?: () => void;
    onAdProgress?: (adProgressData: any) => void;
    onAllAdsCompleted?: () => void;
    // Concurrency session events
    /** Called when concurrency session is established and playback can begin */
    onConcurrencySessionStarted?: (info: SessionStartedInfo) => void;
    /** Called when session is kicked - playback will be stopped */
    onConcurrencyKicked?: (reason: string, message: string) => void;
    /** Called when the number of active sessions changes */
    onConcurrencySessionCountChanged?: (activeSessions: number, limit: number) => void;
    /** Called when concurrency connection state changes */
    onConcurrencyStateChanged?: (state: ConcurrencyConnectionState) => void;
  };
  className?: string;
  settings?: {
    backgroundImageUrl?: string;
    showCountdownAnimation?: boolean;
  };
  auth?: {
    mottoToken?: string;
    userId?: string;
  };
  // TanStack Query options
  queryOptions?: {
    enabled?: boolean;
    staleTime?: number;
    cacheTime?: number;
    retry?: number;
    retryDelay?: number;
  };
  adsEnabled?: boolean;
}

export const CreativeWork: React.FC<CreativeWorkProps> = ({
  publicKey,
  creativeWorkId,
  events,
  hideTitle,
  locale = 'en',
  order,
  filter,
  className,
  settings,
  auth,
  deviceId,
  queryOptions = {},
  adsEnabled = false,
  ...props
}) => {
  // Get the effective deviceId - use provided one or auto-generate
  // This must be consistent between the API call and WebSocket connection
  const effectiveDeviceId = useMemo(() => getDeviceId(deviceId), [deviceId]);

  // CreativeWork data fetching
  const {
    data: creativeWorkData,
    isLoading: isCreativeWorkLoading,
    error: creativeWorkError
  } = useQuery<CreativeWorkData>({
    queryKey: ['creative-work', publicKey, creativeWorkId, filter, order, locale],
    queryFn: () => fetchCreativeWorkData(publicKey, creativeWorkId, undefined, filter, order, locale),
    enabled: !!publicKey && !!creativeWorkId,
    staleTime: queryOptions.staleTime ?? 5 * 60 * 1000, // 5 minutes
    gcTime: queryOptions.cacheTime ?? 10 * 60 * 1000, // 10 minutes
    retry: queryOptions.retry ?? 3,
    retryDelay: queryOptions.retryDelay ?? ((attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000))
  });

  const [activePlaylist, setActivePlaylist] = useState<Playlist | null>();
  const [activeVideoId, setActiveVideoId] = useState<string | null>();
  const [activeConcurrencySession, setActiveConcurrencySession] = useState<ConcurrencySession | undefined>();
  const [showCountDown, setShowCountDown] = useState(false);
  const videoIds = creativeWorkData?.videoIds ?? [];

  // Videos list fetching
  const {
    data: videosData,
    isLoading: videosIsLoading,
    error: videosError,    
  } = useQuery<VideoData[]>({
    queryKey: ['videos-list', publicKey, videoIds, auth?.mottoToken, adsEnabled, locale, effectiveDeviceId],
    queryFn: () => fetchVideosList(publicKey, videoIds, auth?.mottoToken, 0, 0, adsEnabled, locale, effectiveDeviceId),
    enabled: !!publicKey && videoIds.length > 0,
    refetchInterval: activePlaylist === null ? 30000 : false,
    staleTime: queryOptions.staleTime ?? 5 * 60 * 1000,
    gcTime: queryOptions.cacheTime ?? 10 * 60 * 1000,
    retry: queryOptions.retry ?? 3,
    retryDelay: queryOptions.retryDelay ?? ((attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000))
  });

  const [loadingApisState, setLoadingApisState] = useState(true);

  // Process videos data to find active playlist
  useEffect(() => {
    if (videosData !== undefined) {
      setLoadingApisState(false);

      const videosWithPlaylists = videosData.filter(
        (video) => video.playlists && video.playlists.length > 0
      );

      if (videosWithPlaylists.length > 0) {
        let hlsPlaylistFound = false;
        // Iterate to find the first HLS playlist
        for (const video of videosWithPlaylists) {
          const activePlaylist = findHLSPlaylist(video);
          const activePlaylistUrl = (
              activePlaylist?.url ??
              activePlaylist?.drm?.widevine?.playlistUrl ??
              activePlaylist?.drm?.playready?.playlistUrl ??
              activePlaylist?.drm?.fairplay?.playlistUrl);
          const activePlaylistHasUrl = !!activePlaylistUrl;

          if (activePlaylist && activePlaylistHasUrl) {
            setActivePlaylist(activePlaylist);
            setActiveVideoId(video.id);
            setActiveConcurrencySession(video.concurrencySession);
            hlsPlaylistFound = true;
            break;
          }
        }
        if (!hlsPlaylistFound) {
          setActivePlaylist(null);
          setActiveVideoId(null);
          setActiveConcurrencySession(undefined);
        }
      } else {
        setActivePlaylist(null);
        setActiveVideoId(null);
        setActiveConcurrencySession(undefined);
      }
    } else if (creativeWorkData && (!creativeWorkData.videoIds || creativeWorkData.videoIds.length === 0)) {
      setLoadingApisState(false);
      setActivePlaylist(null);
      setActiveVideoId(null);
      setActiveConcurrencySession(undefined);
    }
  }, [videosData, creativeWorkData]);

  const { t } = useMessages(locale);

  // ============================================================================
  // Concurrency Session Management
  // ============================================================================

  /**
   * Connect to concurrency session WebSocket if required.
   *
   * Security: The hook will enforce that playback is only allowed when
   * the WebSocket connection is established (if concurrency control is required).
   * If connection fails or user is kicked, isPlaybackAllowed will be false.
   */
  const concurrencyOptions = useMemo(() => ({
    // Use the same deviceId that was sent to the API
    deviceId: effectiveDeviceId,
    // Forward concurrency events to parent
    onSessionStarted: events?.onConcurrencySessionStarted,
    onKicked: events?.onConcurrencyKicked,
    onSessionCountChanged: events?.onConcurrencySessionCountChanged,
    onStateChanged: events?.onConcurrencyStateChanged,
    onError: (err: Error) => {
      console.error('[CreativeWork] Concurrency session error:', err);
      events?.onError?.(err);
    },
  }), [effectiveDeviceId, events]);

  const {
    isPlaybackAllowed,
    wasKicked,
    kickedReason,
    connectionState: concurrencyConnectionState,
  } = useConcurrencySession(activeConcurrencySession, concurrencyOptions);

  // Notify parent of creative work data
  useEffect(() => {
    if (events?.onCreativeWorkData && creativeWorkData) {
      events.onCreativeWorkData(creativeWorkData);
    }
    if (creativeWorkData && !creativeWorkData?.videoIds?.length) {
      setShowCountDown(true);
    }
  }, [creativeWorkData, events]);

  // Notify parent of video data
  useEffect(() => {
    if (events?.onVideoData && activeVideoId && videosData) {
      const activeVideo = videosData.find((video) => video.id === activeVideoId);
      if (activeVideo) {
        events.onVideoData(activeVideo);
      }
    }
  }, [activeVideoId, videosData, events]);

  const [error, setError] = useState<Error | null>(null);
  const videosDataError = videosData?.some((video) => !!video.error);

  // Error handling
  useEffect(() => {
    // Defer error evaluation until loading states settle to avoid flashing
    // error. Query-level errors must NOT be deferred: they are terminal
    // (retries exhausted) and nothing clears loadingApisState when a fetch
    // fails, so waiting on it would show the loading screen forever.
    const hasFetchError = Boolean(creativeWorkError || videosError);
    if (!hasFetchError && (isCreativeWorkLoading || videosIsLoading || loadingApisState)) {
      return;
    }

    const hasPlayablePlaylist = Boolean(activePlaylist);

    if (creativeWorkError || videosError || (videosDataError && !hasPlayablePlaylist)) {
      const firstVideoError = videosData?.find((video) => !!video.error)?.error;
      const errorObj =
        creativeWorkError ||
        videosError ||
        (firstVideoError && new Error(firstVideoError)) ||
        new Error('default');

      setError(errorObj);
      if (events?.onError) {
        events.onError(errorObj);
      }
    } else {
      setError(null);
    }
  }, [
    creativeWorkError,
    videosError,
    videosDataError,
    videosData,
    events,
    activePlaylist,
    isCreativeWorkLoading,
    videosIsLoading,
    loadingApisState
  ]);

  const [loadingPlaylist, setLoadingPlaylist] = useState(true);

  // Loading state management
  useEffect(() => {
    const creativeWorkLoadedWithNoVideos =
      !isCreativeWorkLoading &&
      creativeWorkData &&
      creativeWorkData.videoIds &&
      creativeWorkData.videoIds.length === 0;

    const creativeWorkLoadedWithNoData =
      !isCreativeWorkLoading && creativeWorkData && !creativeWorkData.videoIds;

    const isEventsFinished =
      !videosIsLoading &&
      videosData &&
      videosData.length > 0 &&
      videosData.every((video) => video.playlists && video.playlists.length === 0);

    if (
      creativeWorkLoadedWithNoVideos ||
      creativeWorkLoadedWithNoData ||
      isEventsFinished
    ) {
      setLoadingPlaylist(false);
      if (events?.onEmptyPlaylists) {
        events.onEmptyPlaylists();
      }
    } else if (activePlaylist) {
      setLoadingPlaylist(false);
    }
  }, [
    isCreativeWorkLoading,
    creativeWorkData,
    videosIsLoading,
    videosData,
    activePlaylist,
    events,
  ]);

  // The error screen takes precedence over the pre-event card: if the creative
  // work's videos are errored we surface the error rather than the countdown.
  // NOTE: all early returns below must stay after every hook above so the hook
  // order never changes between renders (React error #300).
  const releaseTimeMs = creativeWorkData?.releaseTime
    ? new Date(creativeWorkData.releaseTime).getTime()
    : NaN;
  const hasPreEventCard =
    !!creativeWorkData && (showCountDown || releaseTimeMs > Date.now());

  // Error state — takes precedence over the pre-event countdown.
  if (error) {
    const title = t(error.message as keyof Translations)?.length ?
      t(error.message as keyof Translations) : t("DEFAULT_ERROR");
    const description = t(`${error.message}_DESCRIPTION` as keyof Translations)?.length ?
      t(`${error.message}_DESCRIPTION` as keyof Translations) : t("DEFAULT_ERROR_DESCRIPTION");

    return (
      <div className={twMerge("md:rounded-2xl overflow-hidden aspect-video", className)}>
        <div className="relative w-full h-full">
          <ErrorScreen
            title={title}
            description={description}
          />
        </div>
      </div>
    );
  }

  if (hasPreEventCard && creativeWorkData) {
    return (
      <div className={twMerge("", className)}>
        <div className="relative w-full h-full bg-base-200 text-base-content flex justify-center items-center flex-col">
          <PreCreativeWork
            creativeWork={creativeWorkData}
            locale={locale}
            hideTitle={hideTitle}
            backgroundImageUrl={settings?.backgroundImageUrl}
            showAnimation={settings?.showCountdownAnimation}
          />
        </div>
      </div>
    );
  }

  // Loading state
  if (isCreativeWorkLoading || videosIsLoading || loadingApisState) {
    return (
      <div className={twMerge("", className)}>
        <div className="relative w-full aspect-video bg-[#151515]">
          <Loading />
        </div>
      </div>
    );
  }

  // ============================================================================
  // Concurrency Control Check (SECURITY)
  // ============================================================================
  /**
   * If concurrency control is required but playback is not allowed,
   * show an error screen. This prevents bypassing concurrency limits
   * by blocking the WebSocket connection.
   */
  if (activeConcurrencySession && !isPlaybackAllowed) {
    // Determine the appropriate error message
    const isConnecting = concurrencyConnectionState === ConcurrencyConnectionState.CONNECTING;

    // If still connecting, show loading instead of error
    if (isConnecting) {
      return (
        <div className={twMerge("", className)}>
          <div className="relative w-full aspect-video bg-[#151515]">
            <Loading />
          </div>
        </div>
      );
    }

    // User was kicked or connection failed - show error
    // Map kickedReason to appropriate translation keys
    const isConnectionFailure = kickedReason === 'connection_failed';
    const errorTitle = isConnectionFailure
      ? t("CONCURRENCY_CONNECTION_FAILED" as keyof Translations) || "Connection Error"
      : t("CONCURRENCY_KICKED" as keyof Translations) || "Playback Stopped";

    const errorDescription = isConnectionFailure
      ? t("CONCURRENCY_CONNECTION_FAILED_DESCRIPTION" as keyof Translations) || "Unable to establish a connection. Please try again."
      : t("CONCURRENCY_KICKED_DESCRIPTION" as keyof Translations) || "Your session was ended. This may happen if you started watching on another device.";

    return (
      <div className={twMerge("md:rounded-2xl overflow-hidden aspect-video", className)}>
        <div className="relative w-full h-full">
          <ErrorScreen
            title={errorTitle}
            description={errorDescription}
          />
        </div>
      </div>
    );
  }

  // Show player if we have an active playlist
  if (activeVideoId && activePlaylist && !loadingPlaylist) {
    const activeVideo = videosData?.find((video) => video.id === activeVideoId);
    
    return (
      <div className={twMerge("", className)}>
        <div className="relative w-full h-full">
          <Player
            {...props}
            className={twMerge(className, "peer aspect-video")}
            managedMode={true}
            events={{
              ...events,
            }}
            src={activePlaylist}
            locale={locale}
            containerClassName="w-full h-full"
            publicKey={publicKey}
            auth={auth}
            {...(adsEnabled && activeVideo?.ad?.adTagUrl ? { imaConfig: { adTagUrl: activeVideo?.ad?.adTagUrl } } : {})}
          />
          {!hideTitle && (
            <TitleAndDescription
              title={creativeWorkData?.title || ''}
              description={creativeWorkData?.description || ''}
              dateTime={creativeWorkData?.releaseTime || ''}
              locale={locale}
            />
          )}
        </div>
      </div>
    );
  }

  // Loading state for playlist
  if (loadingPlaylist) {
    return (
      <div className={twMerge("", className)}>
        <div className="relative w-full aspect-video bg-[#151515]">
          <Loading />
        </div>
      </div>
    );
  }

  // Empty state
  return null;
};

function PreCreativeWork({ 
  creativeWork, 
  locale = 'en', 
  hideTitle, 
  backgroundImageUrl, 
  showAnimation = true 
}: { 
  creativeWork: CreativeWorkData; 
  locale?: string; 
  hideTitle?: boolean; 
  backgroundImageUrl?: string; 
  showAnimation?: boolean;
}): JSX.Element {
  const date = new Date(creativeWork.releaseTime);
  const now = new Date();
  const [remainingTime, setRemainingTime] = useState(
    date.getTime() - now.getTime()
  );
  const shouldBeStarted = remainingTime < 0;
  const { t } = useMessages(locale);

  useEffect(() => {
    const interval = setInterval(() => {
      if (remainingTime < 0) {
        clearInterval(interval);
      } else {
        setRemainingTime(date.getTime() - new Date().getTime());
      }
    }, 1000);

    return () => { clearInterval(interval); };
  }, [date, remainingTime]);

  const renderCountdown = () => {
    if (shouldBeStarted) {
      return <span className="text-base-content text-xl">{t("EVENT_NOT_STARTED")}</span>;
    }

    const seconds = Math.floor(remainingTime / 1000) % 60;
    const minutes = Math.floor(remainingTime / 1000 / 60) % 60;
    const hours = Math.floor(remainingTime / 1000 / 60 / 60) % 24;
    const days = Math.floor(remainingTime / 1000 / 60 / 60 / 24);

    return (
      <div className="grid grid-flow-col md:gap-5 gap-2 text-center auto-cols-max">
        <div className="flex flex-col">
          {/* daisyUI's `countdown` only supports 2 digits, so days (which can be
              3+) is plain text. Mimic countdown's box model (inline-flex,
              line-height:1em, child height:1em) so it aligns with the others. */}
          <span className="font-mono text-lg md:text-5xl inline-flex leading-[1em] justify-center">
            <span
              className="inline-block h-[1em]"
              aria-live="polite"
              aria-label={days.toString()}
            >
              {days.toString()}
            </span>
          </span>
          <span className="text-xs uppercase tracking-widest mt-1">{t("DAYS")}</span>
        </div>
        <div className="flex flex-col">
          <span className="countdown font-mono text-lg md:text-5xl justify-center">
            <span 
              style={{ "--value": hours } as CSSProperties} 
              aria-live="polite" 
              aria-label={hours.toString()}
            >
              {hours?.toString()?.padStart(2, '0')}
            </span>
          </span>
          <span className="text-xs uppercase tracking-widest mt-1">{t("HOURS")}</span>
        </div>
        <div className="flex flex-col">
          <span className="countdown font-mono text-lg md:text-5xl justify-center">
            <span 
              style={{ "--value": minutes } as CSSProperties} 
              aria-live="polite" 
              aria-label={minutes.toString()}
            >
              {minutes?.toString()?.padStart(2, '0')}
            </span>
          </span>
          <span className="text-xs uppercase tracking-widest mt-1">{t("MINUTES")}</span>
        </div>
        <div className="flex flex-col">
          <span className="countdown font-mono text-lg md:text-5xl justify-center">
            <span 
              style={{ "--value": seconds } as CSSProperties} 
              aria-live="polite" 
              aria-label={seconds.toString()}
            >
              {seconds?.toString()?.padStart(2, '0')}
            </span>
          </span>
          <span className="text-xs uppercase tracking-widest mt-1">{t("SECONDS")}</span>
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className="relative overflow-hidden md:rounded-2xl bg-base-200 aspect-video text-base-content flex flex-col justify-center items-center w-full h-full bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : '',
        }}
      >
        {backgroundImageUrl && <div className="absolute inset-0 bg-black bg-opacity-40"></div>}
        <div className="relative z-10">
          {renderCountdown()}
        </div>
      </div>
      {!hideTitle && (
        <TitleAndDescription
          title={creativeWork.title}
          description={creativeWork.description || ''}
          dateTime={creativeWork.releaseTime}
          locale={locale}
        />
      )}
    </>
  );
}